import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import Button from "../../../components/shared/buttons/button";
import {
  useGetFruitsQuery,
  useGetSuppliersQuery,
  useGenerateBatchCodeMutation,
} from "../../Receiving/unloading/unloadingApi";
import { useCreatePastingSortingMutation, useUpdatePastingSortingMutation } from "./pastingSortingApi";
import toast from "react-hot-toast";
import useTranslator from "../../../hooks/useTranslator";
import { capitalizeFirstLetter, convertToUserTimezone, getCurrentDateTime } from "../../../helpers/Utils";
import usePermissions from "../../../hooks/usePermissions";

const PastingSortingModal = ({ show, setShow, record, mode }) => {
  const { _t } = useTranslator();

  const initialFormData = {
    supplier_id: "",
    fruit_type_id: "",
    fruit_type_variety: "",
    batch_code: "",
    entered_date_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
    total_entered_kg: "",
    accepted_kg: "",
    rejected_kg: "",
    status: "counting",
  };

  const [formData, setFormData] = useState(initialFormData);

  const { checkPermission } = usePermissions();
  const batchCodeEditAccess = checkPermission("pasting.batch_code_editable", "update");

  const [createPastingSorting, { isLoading: isCreating }] = useCreatePastingSortingMutation();
  const [updatePastingSorting, { isLoading: isUpdating }] = useUpdatePastingSortingMutation();

  const { data: supplierList } = useGetSuppliersQuery({ role_id: "brokerage" });
  const { data: fruitList } = useGetFruitsQuery();
  const [generateBatchCodeApi] = useGenerateBatchCodeMutation();

  // Populate form on edit/view
  useEffect(() => {
    if ((mode === "edit" || mode === "view") && record) {
      const updated = { ...initialFormData };
      for (const key in record) {
        if (key in initialFormData) {
          if (key === "entered_date_time" && record[key]) {
            updated[key] = convertToUserTimezone(record[key], "YYYY-MM-DDTHH:mm");
          } else {
            updated[key] = record[key];
          }
        }
      }
      setFormData(updated);
    }
  }, [mode, record]);

  // Auto-generate batch code when supplier changes
  useEffect(() => {
    const fetchBatchCode = async () => {
      if (formData?.supplier_id && supplierList?.data) {
        const supplierName = supplierList.data.find(
          (s) => s.id == formData.supplier_id
        )?.name;

        if (supplierName) {
          try {
            const response = await generateBatchCodeApi({
              supplier_id: formData.supplier_id,
              supplierName,
              type: "brokerage",
            }).unwrap();
            setFormData((prev) => ({
              ...prev,
              batch_code: response?.data?.batch_code || "",
            }));
          } catch (err) {
            console.error("Error generating batch code:", err);
          }
        }
      }
    };

    const supplierChanged = mode === "edit" && record?.supplier_id !== formData.supplier_id;

    if (mode === "add" || supplierChanged) {
      fetchBatchCode();
    } else {
      setFormData((prev) => ({
        ...prev,
        batch_code: record?.batch_code || prev.batch_code,
      }));
    }
  }, [formData?.supplier_id, mode]);

  const handleClose = () => setShow(false);

  // Compute percentages in real time from formData
  const calcPercent = (part, total) => {
    const p = parseFloat(part);
    const t = parseFloat(total);
    if (!t || t === 0 || isNaN(p)) return "0.00";
    return ((p / t) * 100).toFixed(2);
  };

  const acceptedPercent = calcPercent(formData.accepted_kg, formData.total_entered_kg);
  const rejectedPercent = calcPercent(formData.rejected_kg, formData.total_entered_kg);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBatchCodeChange = (e) => {
    const { name, value } = e.target;
    if (name === "batch_code") {
      setFormData((prev) => ({ ...prev, batch_code: value }));
    }
  };

  const validate = (data) => {
    if (!data.supplier_id) {
      return _t("please_select_supplier", "Please select a supplier.");
    }
    if (!data.fruit_type_id) {
      return _t("please_select_fruit_type", "Please select a fruit type.");
    }
    if (!data.fruit_type_variety) {
      return _t("please_select_fruit_type_variety", "Please select a fruit type variety.");
    }
    if (!data.total_entered_kg || parseFloat(data.total_entered_kg) < 1) {
      return _t("total_entered_kg_min", "Total entered weight must be at least 1 kg.");
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreating || isUpdating) return;

    const error = validate(formData);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      if (mode === "add") {
        await createPastingSorting(formData).unwrap();
      } else {
        await updatePastingSorting({ id: record.id, requestBody: formData }).unwrap();
      }
      handleClose();
    } catch (err) {
      console.error("Error saving pasting sorting record:", err);
    }
  };

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <SelectBox
          label={_t("supplier_name", "Supplier Name")}
          name="supplier_id"
          value={formData.supplier_id}
          options={(supplierList?.data || []).map((s) => ({
            value: s.id,
            label: s.name,
          }))}
          onChange={handleChange}
          disabled={mode === "view"}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("entered_date_time", "Entered Date Time")}
          type="datetime-local"
          name="entered_date_time"
          value={formData.entered_date_time}
          onChange={handleChange}
          disabled={true}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("batch_code", "Batch Code")}
          type="text"
          name="batch_code"
          value={formData.batch_code}
          disabled={!batchCodeEditAccess}
          onChange={handleBatchCodeChange}
          placeholder={`${_t("batch_code", "Batch Code")}...`}
        />
      </Col>
      <Col lg={3}>
        <SelectBox
          label={_t("fruit_type", "Fruit Type")}
          name="fruit_type_id"
          options={(fruitList?.data || []).map((f) => ({
            value: f.id,
            label: f.name,
          }))}
          value={formData.fruit_type_id}
          onChange={handleChange}
          disabled={mode === "view"}
        />
      </Col>
      <Col lg={3}>
        <SelectBox
          label={_t("fruit_type_variety", "Fruit Type Variety")}
          name="fruit_type_variety"
          value={formData.fruit_type_variety}
          onChange={handleChange}
          options={["seconds", "pieces"].map((val) => ({
            value: val,
            label: capitalizeFirstLetter(_t(val, val)),
          }))}
          disabled={mode === "view"}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("total_kg_entered", "Total KG Entered")}
          type="number"
          name="total_entered_kg"
          value={formData.total_entered_kg}
          placeholder="0"
          onChange={handleChange}
          disabled={mode === "view"}
        />
      </Col>
      {mode !== "add" && (
        <>
          <Col lg={6}>
            <InputWithLabel
              label={_t("accepted_kg", "Accepted KG")}
              type="number"
              name="accepted_kg"
              value={formData.accepted_kg}
              placeholder="0"
              onChange={handleChange}
              disabled={mode === "view"}
            />
            {formData.total_entered_kg > 0 && (
              <small className="text-muted">{_t("accepted_percent", "Accepted %")}: <strong>{acceptedPercent}%</strong></small>
            )}
          </Col>
          <Col lg={6}>
            <InputWithLabel
              label={_t("rejected_kg", "Rejected KG")}
              type="number"
              name="rejected_kg"
              value={formData.rejected_kg}
              placeholder="0"
              onChange={handleChange}
              disabled={mode === "view"}
            />
            {formData.total_entered_kg > 0 && (
              <small className="text-muted">{_t("rejected_percent", "Rejected %")}: <strong>{rejectedPercent}%</strong></small>
            )}
          </Col>
        </>
      )}
      <Col lg={6}>
        <SelectBox
          label={_t("status", "Status")}
          name="status"
          value={formData.status}
          onChange={handleChange}
          options={[
            { value: "unloading", label: _t("unloading", "Unloading") },
            { value: "sorting",  label: _t("sorting",   "Sorting")   },
            { value: "moved",    label: _t("moved",     "Moved")     },
          ]}
          disabled={mode === "view"}
        />
      </Col>
    </>
  );

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "add"
          ? _t("adding_pasting_sorting", "Adding Material Sorting")
          : _t("editing_pasting_sorting", "Editing Material Sorting")
      }
      children={formData}
    >
      <Row>{renderFormFields()}</Row>

      {mode !== "view" && (
        <Row>
          <Col className="text-end">
            <Button
              label={_t("save_changes", "Save Changes")}
              size="small"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            />
          </Col>
        </Row>
      )}
    </CommonModal>
  );
};

export default PastingSortingModal;
